import React, { useState, useRef, useCallback } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  DocumentTextIcon, FolderIcon, PhotoIcon, TableCellsIcon,
  ArrowDownTrayIcon, ShareIcon, TrashIcon, PlusIcon,
  MagnifyingGlassIcon, EyeIcon, CloudArrowUpIcon,
  DocumentArrowUpIcon, XMarkIcon, CheckIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const FILE_ICONS = {
  pdf:  { Icon: DocumentTextIcon,  color: 'text-red-500',    bg: 'bg-red-50'    },
  doc:  { Icon: DocumentTextIcon,  color: 'text-blue-500',   bg: 'bg-blue-50'   },
  docx: { Icon: DocumentTextIcon,  color: 'text-blue-500',   bg: 'bg-blue-50'   },
  xls:  { Icon: TableCellsIcon,    color: 'text-green-500',  bg: 'bg-green-50'  },
  xlsx: { Icon: TableCellsIcon,    color: 'text-green-500',  bg: 'bg-green-50'  },
  jpg:  { Icon: PhotoIcon,         color: 'text-purple-500', bg: 'bg-purple-50' },
  jpeg: { Icon: PhotoIcon,         color: 'text-purple-500', bg: 'bg-purple-50' },
  png:  { Icon: PhotoIcon,         color: 'text-purple-500', bg: 'bg-purple-50' },
  default: { Icon: FolderIcon,     color: 'text-gray-500',   bg: 'bg-gray-50'   },
};

const CATEGORIES = ['All', 'Invoices', 'Contracts', 'Reports', 'Images', 'Others'];

function getExt(name) { return (name.split('.').pop() || 'default').toLowerCase(); }
function fmtSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}

function FileIcon({ name, size = 'w-8 h-8' }) {
  const ext  = getExt(name);
  const cfg  = FILE_ICONS[ext] || FILE_ICONS.default;
  return (
    <div className={`p-2.5 rounded-xl ${cfg.bg} flex-shrink-0`}>
      <cfg.Icon className={`${size} ${cfg.color}`} />
    </div>
  );
}

function ShareModal({ doc, onClose }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/shared/doc/${doc.id}`;
  const copy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };
  const shareNative = () => {
    if (navigator.share) {
      navigator.share({ title: doc.name, text: `Sharing document: ${doc.name}`, url: link });
    } else { copy(); toast.success('Link copied to clipboard!'); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-gray-900 text-lg">Share Document</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
          <FileIcon name={doc.name} size="w-6 h-6" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-800 truncate">{doc.name}</p>
            <p className="text-xs text-gray-400">{fmtSize(doc.size)}</p>
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Share Link</label>
          <div className="flex gap-2">
            <input readOnly value={link} className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 bg-gray-50 focus:outline-none" />
            <button onClick={copy} className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all ${copied ? 'bg-green-500 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
              {copied ? <CheckIcon className="w-4 h-4" /> : 'Copy'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={shareNative} className="flex items-center justify-center gap-2 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-sm font-semibold transition-colors">
            <ShareIcon className="w-4 h-4" /> Share
          </button>
          <button onClick={() => { handleDownload(doc); onClose(); }} className="flex items-center justify-center gap-2 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold transition-colors">
            <ArrowDownTrayIcon className="w-4 h-4" /> Download
          </button>
        </div>
      </div>
    </div>
  );
}

function handleDownload(doc) {
  const a = document.createElement('a');
  a.href = doc.url;
  a.download = doc.name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  toast.success(`Downloading ${doc.name}`);
}

export function DocumentsPage() {
  const [docs, setDocs]         = useState([
    { id:'1', name:'Q1-Sales-Invoice.pdf',      size:245000,  category:'Invoices',   uploadedAt: new Date('2026-03-01'), url:'#' },
    { id:'2', name:'Annual-Contract-2026.docx', size:182000,  category:'Contracts',  uploadedAt: new Date('2026-02-15'), url:'#' },
    { id:'3', name:'March-Report.xlsx',         size:98000,   category:'Reports',    uploadedAt: new Date('2026-04-01'), url:'#' },
    { id:'4', name:'Site-Photo-001.jpg',        size:1240000, category:'Images',     uploadedAt: new Date('2026-03-20'), url:'#' },
    { id:'5', name:'GST-Filing-Mar26.pdf',      size:320000,  category:'Invoices',   uploadedAt: new Date('2026-03-31'), url:'#' },
  ]);
  const [search,     setSearch]   = useState('');
  const [category,   setCategory] = useState('All');
  const [dragging,   setDragging] = useState(false);
  const [shareDoc,   setShareDoc] = useState(null);
  const [view,       setView]     = useState('grid'); // grid | list
  const [certs,      setCerts]    = useState([]);
  const [certForm,   setCertForm] = useState({ name:'', issuedDate:'', expiryDate:'', file:null, fileName:'' });
  const certInputRef = useRef();

    // Check for certificates expiring within 30 days
  React.useEffect(() => {
    const today = new Date();
    certs.forEach(cert => {
      const expiry = new Date(cert.expiryDate);
      const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      if (daysLeft > 0 && daysLeft <= 30) {
        toast(`⚠️ Certificate "${cert.name}" expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}!`, {
          duration: 8000,
          style: { background: '#fef3c7', color: '#92400e', fontWeight: '600' },
        });
      } else if (daysLeft <= 0) {
        toast.error(`Certificate "${cert.name}" has EXPIRED!`, { duration: 8000 });
      }
    });
  }, [certs]);

  const addCert = () => {
    if (!certForm.name || !certForm.issuedDate || !certForm.expiryDate) {
      toast.error('Please fill certificate name, issued date and expiry date'); return;
    }
    setCerts(prev => [...prev, { ...certForm, id: Date.now() + '' }]);
    setCertForm({ name:'', issuedDate:'', expiryDate:'', file:null, fileName:'' });
    toast.success('Certificate added');
  };

  const deleteCert = id => {
    if (!window.confirm('Delete this certificate?')) return;
    setCerts(prev => prev.filter(c => c.id !== id));
    toast.success('Certificate deleted');
  };

  const certDaysLeft = (expiryDate) => {
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };
  const inputRef = useRef();

  const filtered = docs.filter(d => {
    const matchSearch = d.name.toLowerCase().includes(search.toLowerCase());
    const matchCat    = category === 'All' || d.category === category;
    return matchSearch && matchCat;
  });

  const processFiles = useCallback((files) => {
    const newDocs = Array.from(files).map((f, i) => ({
      id: Date.now() + i + '',
      name: f.name,
      size: f.size,
      category: 'Others',
      uploadedAt: new Date(),
      url: URL.createObjectURL(f),
      file: f,
    }));
    setDocs(prev => [...newDocs, ...prev]);
    toast.success(`${newDocs.length} file${newDocs.length > 1 ? 's' : ''} uploaded`);
  }, []);

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragging(false);
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const onDragOver = e => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const deleteDoc = id => {
    if (!window.confirm('Delete this document?')) return;
    setDocs(prev => prev.filter(d => d.id !== id));
    toast.success('Document deleted');
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Documents" subtitle="Upload, manage and share your business documents" />

      {/* Upload Zone */}
      <div
        onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          dragging ? 'border-indigo-500 bg-indigo-50 scale-[1.01]' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/40'
        }`}>
        <input ref={inputRef} type="file" multiple className="hidden"
          onChange={e => { processFiles(e.target.files); e.target.value = ''; }} />
        <CloudArrowUpIcon className={`w-10 h-10 mx-auto mb-3 ${dragging ? 'text-indigo-600' : 'text-gray-300'}`} />
        <p className="text-sm font-semibold text-gray-600">Drag & drop files here, or <span className="text-indigo-600">browse</span></p>
        <p className="text-xs text-gray-400 mt-1">PDF, DOCX, XLSX, JPG, PNG — up to 50MB each</p>
      </div>

            {/* ── Certificate Section ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="p-2 bg-green-50 rounded-xl">
            <ShieldCheckIcon className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-sm">Certificates</h3>
            <p className="text-xs text-gray-400">Track certification validity & get expiry alerts</p>
          </div>
        </div>

        {/* Add certificate form */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 p-4 bg-gray-50 rounded-xl">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Certificate Name </label>
            <input
              value={certForm.name}
              onChange={e => setCertForm(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. ISO 9001, FSSAI..."
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Issued Date </label>
            <input
              type="date"
              value={certForm.issuedDate}
              onChange={e => setCertForm(p => ({ ...p, issuedDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Expiry Date </label>
            <input
              type="date"
              value={certForm.expiryDate}
              onChange={e => setCertForm(p => ({ ...p, expiryDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Upload File </label>
            <div className="flex gap-2">
              <button
                onClick={() => certInputRef.current?.click()}
                className="flex-1 px-3 py-2 border border-dashed border-gray-300 rounded-xl text-xs text-gray-500 hover:border-indigo-400 hover:bg-indigo-50 transition-colors truncate">
                {certForm.fileName || 'Choose file'}
              </button>
              <button onClick={addCert}
                className="px-3 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors">
                <PlusIcon className="w-4 h-4" />
              </button>
            </div>
            <input ref={certInputRef} type="file" className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={e => {
                const f = e.target.files[0];
                if (f) setCertForm(p => ({ ...p, file: f, fileName: f.name }));
                e.target.value = '';
              }} />
          </div>
        </div>

        {/* Certificate list */}
        {certs.length === 0 ? (
          <p className="text-center text-xs text-gray-400 py-4">No certificates added yet</p>
        ) : (
          <div className="space-y-2">
            {certs.map(cert => {
              const days = certDaysLeft(cert.expiryDate);
              const isExpired  = days <= 0;
              const isWarning  = days > 0 && days <= 30;
              const isOk       = days > 30;
              return (
                <div key={cert.id} className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
                  isExpired ? 'bg-red-50 border-red-200' : isWarning ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-100'}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <ShieldCheckIcon className={`w-5 h-5 flex-shrink-0 ${isExpired ? 'text-red-500' : isWarning ? 'text-amber-500' : 'text-green-500'}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{cert.name}</p>
                      <p className="text-xs text-gray-500">
                        Issued: {fmtDate(cert.issuedDate)} &nbsp;·&nbsp; Expires: {fmtDate(cert.expiryDate)}
                        {cert.fileName && <span className="ml-2 text-indigo-500">📎 {cert.fileName}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      isExpired ? 'bg-red-100 text-red-700' : isWarning ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                      {isExpired ? 'EXPIRED' : isWarning ? `${days}d left` : `Valid · ${days}d`}
                    </span>
                    <button onClick={() => deleteCert(cert.id)} className="p-1.5 hover:bg-white rounded-lg text-gray-400 hover:text-red-500 transition-colors">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters & toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search documents..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                category === c ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {c}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {['grid','list'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${view === v ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
              {v === 'grid' ? '⊞' : '≡'} {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Files', value: docs.length, color: 'bg-indigo-50 text-indigo-700' },
          { label: 'Total Size',  value: fmtSize(docs.reduce((a,d) => a + d.size, 0)), color: 'bg-blue-50 text-blue-700' },
          { label: 'Invoices',    value: docs.filter(d => d.category === 'Invoices').length, color: 'bg-green-50 text-green-700' },
          { label: 'Contracts',   value: docs.filter(d => d.category === 'Contracts').length, color: 'bg-amber-50 text-amber-700' },
        ].map(s => (
          <div key={s.label} className={`rounded-2xl p-4 ${s.color}`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium opacity-70 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Document Grid / List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <DocumentArrowUpIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No documents found</p>
          <p className="text-xs mt-1">Upload files or change your search filter</p>
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(doc => (
            <div key={doc.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-4 group">
              <div className="flex items-start justify-between mb-3">
                <FileIcon name={doc.name} />
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{doc.category}</span>
              </div>
              <p className="text-sm font-semibold text-gray-800 truncate mb-1" title={doc.name}>{doc.name}</p>
              <p className="text-xs text-gray-400 mb-4">{fmtSize(doc.size)} · {fmtDate(doc.uploadedAt)}</p>
              <div className="flex gap-2">
                <button onClick={() => handleDownload(doc)}
                  className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-semibold transition-colors">
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Download
                </button>
                <button onClick={() => setShareDoc(doc)}
                  className="p-1.5 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-xl transition-colors">
                  <ShareIcon className="w-4 h-4" />
                </button>
                <button onClick={() => deleteDoc(doc.id)}
                  className="p-1.5 bg-gray-50 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-xl transition-colors">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Size</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(doc => (
                <tr key={doc.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <FileIcon name={doc.name} size="w-5 h-5" />
                      <span className="font-medium text-gray-800 truncate max-w-xs">{doc.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{doc.category}</span>
                  </td>
                  <td className="px-5 py-3 text-gray-500">{fmtSize(doc.size)}</td>
                  <td className="px-5 py-3 text-gray-400 text-xs">{fmtDate(doc.uploadedAt)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => handleDownload(doc)} className="p-1.5 hover:bg-indigo-50 text-indigo-600 rounded-lg transition-colors">
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => setShareDoc(doc)} className="p-1.5 hover:bg-gray-100 text-gray-500 rounded-lg transition-colors">
                        <ShareIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteDoc(doc.id)} className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg transition-colors">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {shareDoc && <ShareModal doc={shareDoc} onClose={() => setShareDoc(null)} />}
    </div>
  );
}
